import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import useSWR from 'swr';
import axios from 'axios';
import { styled } from '@mui/system';
import { getRootDomain } from '../utils/domain';
import { BranchLandingSettings } from './BranchLandingSettings';
import { apiHost } from '../utils/apiHost';
import { notif, konfirmasi } from '../utils/dialog';
// Styled component for image display
const StyledImage = styled('img')({
  width: '100px',
  height: 'auto',
  borderRadius: '4px',
});

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

// Create an Axios instance with the dynamic base URL and credentials
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

// Fetcher function for useSWR
const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

// Kartu pengaturan PPN untuk admin cabang: hanya mengatur PPN cabangnya sendiri
const PpnBranchCard = ({ cabangUuid }) => {
  const { data, isLoading, mutate } = useSWR(
    cabangUuid ? `/getppncabang?cabangUuid=${cabangUuid}` : null,
    fetcher
  );
  const [ppnAktif, setPpnAktif] = useState(false);
  const [ppnPersen, setPpnPersen] = useState(11);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setPpnAktif(!!data.data.ppnAktif);
      setPpnPersen(data.data.ppnPersen ?? 11);
    }
  }, [data]);

  const handleSave = async (nextAktif = ppnAktif, nextPersen = ppnPersen) => {
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updateppn', {
        cabangUuid,
        ppnAktif: nextAktif,
        ppnPersen: parseFloat(nextPersen),
      });
      mutate();
      setFeedback({ type: 'success', msg: 'Pengaturan PPN berhasil disimpan.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan PPN.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (e) => {
    const nextAktif = e.target.checked;
    setPpnAktif(nextAktif);
    handleSave(nextAktif, ppnPersen);
  };

  if (!cabangUuid) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Pengaturan PPN Cabang" subheader="PPN akan diterapkan ke seluruh transaksi cabang ini, termasuk struk." />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Switch checked={ppnAktif} onChange={handleToggle} disabled={saving} />}
                label={ppnAktif ? 'PPN Aktif' : 'PPN Nonaktif'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Persentase PPN"
                value={ppnPersen}
                disabled={!ppnAktif || saving}
                onChange={(e) => setPpnPersen(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, max: 100, step: 0.5 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || !ppnAktif}
                onClick={() => handleSave()}
              >
                Simpan
              </Button>
            </Grid>
            {feedback && (
              <Grid item xs={12}>
                <Alert severity={feedback.type}>{feedback.msg}</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// Kartu konfigurasi nota (telp, wifi, teks struk) untuk admin cabang: hanya mengatur cabangnya sendiri
const NotaBranchCard = ({ cabangUuid }) => {
  const { data, isLoading, mutate } = useSWR(
    cabangUuid ? `/getkonfigurasicabang?cabangUuid=${cabangUuid}` : null,
    (url) => axiosInstance.get(url).then((res) => res.data).catch((err) => {
      if (err.response?.status === 404) return { status: true, data: null };
      throw err;
    })
  );
  const { data: cabangData, mutate: mutateCabang } = useSWR(
    cabangUuid ? `/cabang/${cabangUuid}` : null,
    fetcher
  );
  const [form, setForm] = useState({ noTelp: '' });
  const [cabangForm, setCabangForm] = useState({ namacabang: '', alamat: '', slug: '' });
  const [slugError, setSlugError] = useState('');
  const [toggles, setToggles] = useState({
    logoTampilAplikasi: true, logoTampilWebsite: true, logoTampilMenu: true,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setForm({ noTelp: data.data.noTelp || '' });
      setToggles({
        logoTampilAplikasi: data.data.logoTampilAplikasi !== false,
        logoTampilWebsite: data.data.logoTampilWebsite !== false,
        logoTampilMenu: data.data.logoTampilMenu !== false,
      });
    }
  }, [data]);

  useEffect(() => {
    if (cabangData?.data) {
      setCabangForm({
        namacabang: cabangData.data.namacabang || '',
        alamat: cabangData.data.alamat || '',
        slug: cabangData.data.slug || '',
      });
    }
  }, [cabangData]);

  const currentLogoUrl = data?.data?.logo ? `${axiosInstance.defaults.baseURL}/uploads/${data.data.logo}` : null;
  const currentFaviconUrl = data?.data?.favicon ? `${axiosInstance.defaults.baseURL}/uploads/${data.data.favicon}` : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCabangChange = (e) => {
    const { name, value } = e.target;
    if (name === 'slug') setSlugError('');
    setCabangForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    setSlugError('');
    try {
      const formData = new FormData();
      formData.append('cabangUuid', cabangUuid);
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      Object.entries(toggles).forEach(([key, value]) => formData.append(key, value));
      if (logoFile) formData.append('logo', logoFile);
      if (faviconFile) formData.append('favicon', faviconFile);

      await Promise.all([
        axiosInstance.put('/updatekonfigurasicabang', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
        axiosInstance.put(`/updatecabang/${cabangUuid}`, {
          namacabang: cabangForm.namacabang,
          alamat: cabangForm.alamat,
          slug: cabangForm.slug,
        }),
      ]);
      mutate();
      mutateCabang();
      setLogoFile(null);
      setLogoPreview(null);
      setFaviconFile(null);
      setFaviconPreview(null);
      setFeedback({ type: 'success', msg: 'Konfigurasi berhasil disimpan.' });
    } catch (err) {
      if (err.response?.status === 409) {
        setSlugError(err.response.data.message);
      }
      setFeedback({
        type: 'error',
        msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan konfigurasi.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async (type) => {
    if (!await konfirmasi(`Hapus ${type === 'logo' ? 'logo' : 'favicon'} cabang ini? Setelah dihapus akan memakai logo utama (jika ada).`)) return;
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updatekonfigurasicabang', {
        cabangUuid,
        [type === 'logo' ? 'removeLogo' : 'removeFavicon']: true,
      });
      mutate();
      if (type === 'logo') {
        setLogoFile(null);
        setLogoPreview(null);
      } else {
        setFaviconFile(null);
        setFaviconPreview(null);
      }
      setFeedback({ type: 'success', msg: `${type === 'logo' ? 'Logo' : 'Favicon'} cabang berhasil dihapus.` });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menghapus.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!cabangUuid) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Identitas & Logo Cabang" subheader="Nama, alamat, kontak, logo dan favicon cabang. Isi dan lebar struk (WiFi, teks, catatan kaki) diatur di menu Struk." />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nama Cabang"
                name="namacabang"
                value={cabangForm.namacabang}
                onChange={handleCabangChange}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Alamat Cabang"
                name="alamat"
                value={cabangForm.alamat}
                onChange={handleCabangChange}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug / Subdomain Menu"
                name="slug"
                value={cabangForm.slug}
                onChange={handleCabangChange}
                disabled={saving}
                error={Boolean(slugError)}
                helperText={slugError || `Menu cabang bisa diakses di ${(cabangForm.slug || 'slug').toLowerCase()}.${getRootDomain()}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nomor Telepon"
                name="noTelp"
                value={form.noTelp}
                onChange={handleChange}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Logo Cabang</Typography>
              <Typography variant="caption" color="text.secondary">
                Kosongkan untuk memakai logo default sistem.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{
                  width: 70, height: 70, border: '1px solid #ddd', borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa'
                }}>
                  {(logoPreview || currentLogoUrl) ? (
                    <img src={logoPreview || currentLogoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <ImageIcon sx={{ color: '#ccc' }} />
                  )}
                </Box>
                <Button variant="outlined" component="label" size="small" disabled={saving}>
                  Pilih Logo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setLogoFile(file || null);
                      setLogoPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                </Button>
                {currentLogoUrl && !logoFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    disabled={saving}
                    onClick={() => handleRemoveLogo('logo')}
                  >
                    Hapus
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{
                  width: 40, height: 40, border: '1px solid #ddd', borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa'
                }}>
                  {(faviconPreview || currentFaviconUrl) ? (
                    <img src={faviconPreview || currentFaviconUrl} alt="Favicon" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <ImageIcon sx={{ color: '#ccc', fontSize: 18 }} />
                  )}
                </Box>
                <Button variant="outlined" component="label" size="small" disabled={saving}>
                  Pilih Favicon
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFaviconFile(file || null);
                      setFaviconPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                </Button>
                {currentFaviconUrl && !faviconFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    disabled={saving}
                    onClick={() => handleRemoveLogo('favicon')}
                  >
                    Hapus
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Tampilkan Logo Di</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={toggles.logoTampilAplikasi}
                    onChange={(e) => setToggles((p) => ({ ...p, logoTampilAplikasi: e.target.checked }))}
                    disabled={saving}
                  />
                }
                label="Aplikasi"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={toggles.logoTampilWebsite}
                    onChange={(e) => setToggles((p) => ({ ...p, logoTampilWebsite: e.target.checked }))}
                    disabled={saving}
                  />
                }
                label="Website"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={toggles.logoTampilMenu}
                    onChange={(e) => setToggles((p) => ({ ...p, logoTampilMenu: e.target.checked }))}
                    disabled={saving}
                  />
                }
                label="Web Menu"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                Simpan
              </Button>
            </Grid>
            {feedback && (
              <Grid item xs={12}>
                <Alert severity={feedback.type}>{feedback.msg}</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// Kartu logo & favicon utama (fallback global), khusus superadmin
const PengaturanUtamaCard = () => {
  const { data, isLoading, mutate } = useSWR('/getpengaturanutama', fetcher);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const currentLogoUrl = data?.data?.logo ? `${axiosInstance.defaults.baseURL}/uploads/${data.data.logo}` : null;
  const currentFaviconUrl = data?.data?.favicon ? `${axiosInstance.defaults.baseURL}/uploads/${data.data.favicon}` : null;

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      if (logoFile) formData.append('logo', logoFile);
      if (faviconFile) formData.append('favicon', faviconFile);
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      mutate();
      setLogoFile(null);
      setLogoPreview(null);
      setFaviconFile(null);
      setFaviconPreview(null);
      setFeedback({ type: 'success', msg: 'Logo & favicon utama berhasil disimpan.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (type) => {
    if (!await konfirmasi(`Hapus ${type === 'logo' ? 'logo' : 'favicon'} utama?`)) return;
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updatepengaturanutama', {
        [type === 'logo' ? 'removeLogo' : 'removeFavicon']: true,
      });
      mutate();
      if (type === 'logo') {
        setLogoFile(null);
        setLogoPreview(null);
      } else {
        setFaviconFile(null);
        setFaviconPreview(null);
      }
      setFeedback({ type: 'success', msg: `${type === 'logo' ? 'Logo' : 'Favicon'} utama berhasil dihapus.` });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menghapus.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Logo & Favicon Utama"
        subheader="Dipakai sebagai fallback di semua cabang yang belum upload logo/favicon sendiri."
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>Logo Utama</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{
                  width: 80, height: 80, border: '1px solid #ddd', borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa'
                }}>
                  {(logoPreview || currentLogoUrl) ? (
                    <img src={logoPreview || currentLogoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <ImageIcon sx={{ color: '#ccc' }} />
                  )}
                </Box>
                <Button variant="outlined" component="label" disabled={saving}>
                  Pilih Logo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setLogoFile(file || null);
                      setLogoPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                </Button>
                {currentLogoUrl && !logoFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={saving}
                    onClick={() => handleRemove('logo')}
                  >
                    Hapus
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>Favicon Utama</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{
                  width: 40, height: 40, border: '1px solid #ddd', borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa'
                }}>
                  {(faviconPreview || currentFaviconUrl) ? (
                    <img src={faviconPreview || currentFaviconUrl} alt="Favicon" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <ImageIcon sx={{ color: '#ccc', fontSize: 18 }} />
                  )}
                </Box>
                <Button variant="outlined" component="label" disabled={saving}>
                  Pilih Favicon
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFaviconFile(file || null);
                      setFaviconPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                </Button>
                {currentFaviconUrl && !faviconFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={saving}
                    onClick={() => handleRemove('favicon')}
                  >
                    Hapus
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || (!logoFile && !faviconFile)}
                onClick={handleSave}
              >
                Simpan
              </Button>
            </Grid>
            {feedback && (
              <Grid item xs={12}>
                <Alert severity={feedback.type}>{feedback.msg}</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// Lama sesi login, khusus superadmin. Berlaku untuk semua pengguna (web & aplikasi kasir):
// sesi yang tidak dipakai selama N hari otomatis keluar, setiap pemakaian memperpanjangnya lagi.
// Alamat aplikasi menu pelanggan (dulu FRONTEND_URL di .env server): dipakai QR meja & panduan Midtrans.
const AlamatWebMenuCard = () => {
  const { data, isLoading, mutate } = useSWR('/getpengaturanutama', fetcher);
  const [alamat, setAlamat] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.data) setAlamat(data.data.webMenuUrl || '');
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updatepengaturanutama', { webMenuUrl: alamat });
      mutate();
      setFeedback({ type: 'success', msg: 'Alamat web menu disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Alamat Web Menu"
        subheader="Alamat aplikasi menu pelanggan. Dipakai untuk QR meja dan alamat yang dipasang di dashboard Midtrans."
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Alamat web menu"
                size="small"
                value={alamat}
                onChange={(e) => setAlamat(e.target.value)}
                helperText="Contoh: https://menu.domainanda.com"
                sx={{ width: 360 }}
              />
              <Button variant="contained" onClick={handleSave} disabled={saving || alamat === (data?.data?.webMenuUrl || '')} sx={{ mt: 0.25 }}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </Box>
            {feedback && <Alert severity={feedback.type} sx={{ mt: 2 }}>{feedback.msg}</Alert>}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const KontakLanggananCard = () => {
  const { data, isLoading, mutate } = useSWR('/getpengaturanutama', fetcher);
  const [nomor, setNomor] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const tersimpan = data?.data?.kontakLangganan || '';

  useEffect(() => {
    if (data?.data) setNomor(data.data.kontakLangganan || '');
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updatepengaturanutama', { kontakLangganan: nomor });
      mutate();
      setFeedback({ type: 'success', msg: 'Nomor kontak perpanjangan disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Kontak Perpanjangan Langganan"
        subheader="Nomor WhatsApp yang ditampilkan di pesan 'masa aktif cabang berakhir' dan banner peringatan admin cabang."
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Nomor WhatsApp"
                size="small"
                value={nomor}
                onChange={(e) => setNomor(e.target.value)}
                helperText="Contoh: 085721999190 atau 6285721999190"
                sx={{ width: 280 }}
              />
              <Button variant="contained" onClick={handleSave} disabled={saving || !nomor || nomor === tersimpan} sx={{ mt: 0.25 }}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </Box>
            {feedback && <Alert severity={feedback.type} sx={{ mt: 2 }}>{feedback.msg}</Alert>}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const SessionSettingCard = () => {
  const { data, isLoading, mutate } = useSWR('/getpengaturanutama', fetcher);
  const [days, setDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const savedDays = data?.data?.sessionDurationDays ?? 30;

  useEffect(() => {
    if (data?.data) setDays(String(data.data.sessionDurationDays ?? 30));
  }, [data]);

  const parsed = parseInt(days, 10);
  const invalid = Number.isNaN(parsed) || parsed < 1 || parsed > 365;

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updatepengaturanutama', { sessionDurationDays: parsed });
      mutate();
      setFeedback({ type: 'success', msg: `Lama sesi login disimpan: ${parsed} hari.` });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Keamanan Login"
        subheader="Berapa hari login bertahan sejak terakhir dipakai. Setelah itu pengguna otomatis keluar dan harus login ulang (aplikasi POS akan logout otomatis)."
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Lama sesi login"
                type="number"
                size="small"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                inputProps={{ min: 1, max: 365 }}
                InputProps={{ endAdornment: <InputAdornment position="end">hari</InputAdornment> }}
                error={days !== '' && invalid}
                helperText={days !== '' && invalid ? 'Isi antara 1 sampai 365 hari' : 'Disarankan 30 hari untuk perangkat kasir'}
                sx={{ width: 220 }}
              />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || invalid || parsed === savedDays}
                sx={{ mt: 0.25 }}
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              Makin lama durasinya, makin lama perangkat yang hilang/dicuri bisa dipakai tanpa login ulang.
              Perubahan berlaku untuk semua sesi pada pemakaian berikutnya.
            </Alert>
            {feedback && (
              <Alert severity={feedback.type} sx={{ mt: 1 }}>{feedback.msg}</Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Satu baris logo/favicon cabang di tabel superadmin, dengan state upload/hapus sendiri
const LogoCabangRow = ({ row, onSaved }) => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingToggle, setSavingToggle] = useState(null);

  const currentLogoUrl = row.logo ? `${axiosInstance.defaults.baseURL}/uploads/${row.logo}` : null;
  const currentFaviconUrl = row.favicon ? `${axiosInstance.defaults.baseURL}/uploads/${row.favicon}` : null;

  const handleToggle = async (field, value) => {
    setSavingToggle(field);
    try {
      await axiosInstance.put('/updatekonfigurasicabang', {
        cabangUuid: row.cabangUuid,
        [field]: value,
      });
      onSaved?.();
    } catch (err) {
      notif.error(err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan pengaturan tampilan.');
    } finally {
      setSavingToggle(null);
    }
  };

  const uploadNow = async (formExtra) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('cabangUuid', row.cabangUuid);
      if (logoFile) formData.append('logo', logoFile);
      if (faviconFile) formData.append('favicon', faviconFile);
      Object.entries(formExtra || {}).forEach(([key, value]) => formData.append(key, value));
      await axiosInstance.put('/updatekonfigurasicabang', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoFile(null);
      setLogoPreview(null);
      setFaviconFile(null);
      setFaviconPreview(null);
      onSaved?.();
    } catch (err) {
      notif.error(err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan logo/favicon cabang.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (type) => {
    if (!await konfirmasi(`Hapus ${type === 'logo' ? 'logo' : 'favicon'} cabang ${row.namacabang}?`)) return;
    setSaving(true);
    try {
      await axiosInstance.put('/updatekonfigurasicabang', {
        cabangUuid: row.cabangUuid,
        [type === 'logo' ? 'removeLogo' : 'removeFavicon']: true,
      });
      onSaved?.();
    } catch (err) {
      notif.error(err.response?.data?.msg || err.response?.data?.message || 'Gagal menghapus logo/favicon cabang.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow>
      <TableCell>{row.namacabang}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 44, height: 44, border: '1px solid #ddd', borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa', flexShrink: 0,
          }}>
            {(logoPreview || currentLogoUrl) ? (
              <img src={logoPreview || currentLogoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ImageIcon sx={{ color: '#ccc', fontSize: 18 }} />
            )}
          </Box>
          <Button variant="outlined" component="label" size="small" disabled={saving}>
            Pilih
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setLogoFile(file || null);
                setLogoPreview(file ? URL.createObjectURL(file) : null);
              }}
            />
          </Button>
          {logoFile && (
            <Button size="small" variant="contained" disabled={saving} onClick={() => uploadNow()}>
              Simpan
            </Button>
          )}
          {currentLogoUrl && !logoFile && (
            <IconButton size="small" color="error" disabled={saving} onClick={() => handleRemove('logo')}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 36, height: 36, border: '1px solid #ddd', borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa', flexShrink: 0,
          }}>
            {(faviconPreview || currentFaviconUrl) ? (
              <img src={faviconPreview || currentFaviconUrl} alt="Favicon" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <ImageIcon sx={{ color: '#ccc', fontSize: 16 }} />
            )}
          </Box>
          <Button variant="outlined" component="label" size="small" disabled={saving}>
            Pilih
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setFaviconFile(file || null);
                setFaviconPreview(file ? URL.createObjectURL(file) : null);
              }}
            />
          </Button>
          {faviconFile && (
            <Button size="small" variant="contained" disabled={saving} onClick={() => uploadNow()}>
              Simpan
            </Button>
          )}
          {currentFaviconUrl && !faviconFile && (
            <IconButton size="small" color="error" disabled={saving} onClick={() => handleRemove('favicon')}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {[
            { field: 'logoTampilStruk', label: 'Struk' },
            { field: 'logoTampilAplikasi', label: 'Aplikasi' },
            { field: 'logoTampilWebsite', label: 'Website' },
            { field: 'logoTampilMenu', label: 'Web Menu' },
          ].map(({ field, label }) => (
            <FormControlLabel
              key={field}
              sx={{ mr: 0 }}
              control={
                <Switch
                  size="small"
                  checked={row[field] !== false}
                  disabled={savingToggle === field}
                  onChange={(e) => handleToggle(field, e.target.checked)}
                />
              }
              label={<Typography variant="caption">{label}</Typography>}
            />
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
};

// Daftar logo & favicon tiap cabang, khusus superadmin: bisa lihat sekaligus upload/hapus langsung per cabang
const LogoAllCabangCard = () => {
  const { data, isLoading, mutate } = useSWR('/getlogoallcabang', fetcher);

  const rows = (data?.data || []).map((k) => ({
    cabangUuid: k.cabangUuid,
    namacabang: k.namacabang || '-',
    logo: k.logo,
    favicon: k.favicon,
    logoTampilStruk: k.logoTampilStruk,
    logoTampilAplikasi: k.logoTampilAplikasi,
    logoTampilWebsite: k.logoTampilWebsite,
    logoTampilMenu: k.logoTampilMenu,
  }));

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Logo & Favicon Semua Cabang" subheader="Lihat, upload, atau hapus logo/favicon tiap cabang langsung dari sini." />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Cabang</TableCell>
              <TableCell>Logo</TableCell>
              <TableCell>Favicon</TableCell>
              <TableCell>Tampil Di</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Belum ada data cabang.</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <LogoCabangRow key={row.cabangUuid} row={row} onSaved={mutate} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

// Kartu ringkasan PPN semua cabang, khusus superadmin
const PpnAllCabangCard = () => {
  const { data, isLoading, mutate } = useSWR('/getppnallcabang', fetcher);
  const [editRows, setEditRows] = useState({});
  const [savingUuid, setSavingUuid] = useState(null);

  const getRowValue = (row) =>
    editRows[row.cabangUuid] || { ppnAktif: row.ppnAktif, ppnPersen: row.ppnPersen };

  const setRowValue = (cabangUuid, changes) => {
    setEditRows((prev) => ({
      ...prev,
      [cabangUuid]: { ...getRowValueFromData(cabangUuid), ...prev[cabangUuid], ...changes },
    }));
  };

  const getRowValueFromData = (cabangUuid) => {
    const row = data?.data?.find((r) => r.cabangUuid === cabangUuid);
    return row ? { ppnAktif: row.ppnAktif, ppnPersen: row.ppnPersen } : { ppnAktif: false, ppnPersen: 11 };
  };

  const handleSaveRow = async (cabangUuid) => {
    const row = data?.data?.find((r) => r.cabangUuid === cabangUuid);
    const value = row ? getRowValue(row) : { ppnAktif: false, ppnPersen: 11 };
    setSavingUuid(cabangUuid);
    try {
      await axiosInstance.put('/updateppn', {
        cabangUuid,
        ppnAktif: value.ppnAktif,
        ppnPersen: parseFloat(value.ppnPersen),
      });
      mutate();
    } catch (err) {
      notif.error(err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan PPN.');
    } finally {
      setSavingUuid(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="PPN Semua Cabang" subheader="Ringkasan status PPN tiap cabang (khusus super admin)" />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Cabang</TableCell>
              <TableCell>Status PPN</TableCell>
              <TableCell>Persentase</TableCell>
              <TableCell>Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : data?.data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Belum ada data cabang.</TableCell>
              </TableRow>
            ) : (
              data?.data?.map((row) => {
                const value = getRowValue(row);
                return (
                  <TableRow key={row.cabangUuid}>
                    <TableCell>{row.namacabang}</TableCell>
                    <TableCell>
                      <Switch
                        checked={!!value.ppnAktif}
                        onChange={(e) => setRowValue(row.cabangUuid, { ppnAktif: e.target.checked })}
                        disabled={savingUuid === row.cabangUuid}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={value.ppnPersen}
                        onChange={(e) => setRowValue(row.cabangUuid, { ppnPersen: e.target.value })}
                        disabled={savingUuid === row.cabangUuid}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          inputProps: { min: 0, max: 100, step: 0.5 },
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        disabled={savingUuid === row.cabangUuid}
                        onClick={() => handleSaveRow(row.cabangUuid)}
                      >
                        Simpan
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export const Konfigurasi = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === 'superadmin';
  const branchCabangUuid = user?.cabang?.uuid || user?.cabanguuid || '';
  // SWR hooks for fetching Konfigurasi and Cabang data
  const {
    data: konfigurasiData,
    error: konfigurasiError,
    isLoading: isLoadingKonfigurasi,
    mutate: mutateKonfigurasi,
  } = useSWR(isSuperAdmin ? '/getkonfigurasi' : null, fetcher);
  const {
    data: cabangData,
    error: cabangError,
    isLoading: isLoadingCabang,
  } = useSWR('/cabang', fetcher); // Assuming a '/cabang' endpoint exists for branch data

  // State for dialog management
  const [openDialog, setOpenDialog] = useState(false);
  const [currentKonfigurasi, setCurrentKonfigurasi] = useState(null);
  const [selectedCabangForLanding, setSelectedCabangForLanding] = useState('');

  // State for form values
  const [formValues, setFormValues] = useState({
    noTelp: '',
    wifiNama: '',
    wifi: '',
    teksA: '',
    teksB: '',
    cabangUuid: '', // Used for selecting branch when creating
    logo: null, // File object for upload
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  useEffect(() => {
    if (currentKonfigurasi) {
      setFormValues({
        noTelp: currentKonfigurasi.noTelp || '',
        wifiNama: currentKonfigurasi.wifiNama || '',
        wifi: currentKonfigurasi.wifi || '',
        teksA: currentKonfigurasi.teksA || '',
        teksB: currentKonfigurasi.teksB || '',
        cabangUuid: currentKonfigurasi.Cabang ? currentKonfigurasi.Cabang.uuid : '',
        logo: null,
      });

      setLogoPreview(
        currentKonfigurasi.logo
          ? `${axiosInstance.defaults.baseURL}/uploads/${currentKonfigurasi.logo}`
          : null
      );
    } else {

      setFormValues({
        noTelp: '',
        wifiNama: '',
        wifi: '',
        teksA: '',
        teksB: '',
        cabangUuid: '',
        logo: null,
      });
      setLogoPreview(null);
    }
    setFormErrors({});
  }, [currentKonfigurasi]);

 
  const handleOpenDialog = (konfigurasi = null) => {
    setCurrentKonfigurasi(konfigurasi);
    setOpenDialog(true);
  };

 
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentKonfigurasi(null);
    setFormValues({
      noTelp: '',
      wifiNama: '',
      wifi: '',
      teksA: '',
      teksB: '',
      cabangUuid: '',
      logo: null,
    });
    setLogoPreview(null);
    setFormErrors({});
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormValues((prev) => ({ ...prev, logo: file }));
    if (file) {
      setLogoPreview(URL.createObjectURL(file)); 
    } else {
      setLogoPreview(null);
    }
  };

  // Basic form validation
  const validateForm = () => {
    let errors = {};
    // Cabang (branch) is required only when creating a new configuration
    if (!formValues.cabangUuid && !currentKonfigurasi) {
      errors.cabangUuid = 'Cabang harus dipilih.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Returns true if no errors
  };

 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const formData = new FormData();
    formData.append('noTelp', formValues.noTelp);
    formData.append('wifiNama', formValues.wifiNama);
    formData.append('wifi', formValues.wifi);
    formData.append('teksA', formValues.teksA);
    formData.append('teksB', formValues.teksB);
    if (formValues.logo) {
      formData.append('logo', formValues.logo); 
    }

    try {
      if (currentKonfigurasi) {
        // Update existing Konfigurasi
        await axiosInstance.put(`/updatekonfigurasi/${currentKonfigurasi.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data', 
          },
        });
      } else {
        formData.append('cabangUuid', formValues.cabangUuid); 
        await axiosInstance.post('/createkonfigurasi', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      mutateKonfigurasi(); // Revalidate SWR cache to update the table
      handleCloseDialog(); 
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err.response && err.response.data && err.response.data.msg) {
        setFormErrors({ general: err.response.data.msg });
      } else {
        setFormErrors({ general: 'Terjadi kesalahan saat menyimpan data. Mohon coba lagi.' });
      }
    }
  };

  // Handle deletion of a Konfigurasi
  const handleDelete = async (id) => {
    if (await konfirmasi('Apakah Anda yakin ingin menghapus konfigurasi ini?')) {
      try {
        await axiosInstance.delete(`/deletekonfigurasi/${id}`);
        mutateKonfigurasi();
      } catch (err) {
        console.error('Error deleting konfigurasi:', err);
        notif.error('Gagal menghapus konfigurasi. Silakan coba lagi.'); 
      }
    }
  };

  
  // Loading and Error states
  

  return (
    <Box sx={{ p: 3 }}>
      {isSuperAdmin ? (
        <>
          <PengaturanUtamaCard />
          <SessionSettingCard />
          <KontakLanggananCard />
          <AlamatWebMenuCard />
          <LogoAllCabangCard />
          <PpnAllCabangCard />
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Halaman Menu Cabang"
              subheader="Pilih cabang untuk mengatur halaman menu (landing page) cabang tersebut."
            />
            <CardContent>
              <FormControl sx={{ minWidth: 280, mb: selectedCabangForLanding ? 2 : 0 }}>
                <InputLabel id="cabang-landing-select-label">Pilih Cabang</InputLabel>
                <Select
                  labelId="cabang-landing-select-label"
                  label="Pilih Cabang"
                  value={selectedCabangForLanding}
                  onChange={(e) => setSelectedCabangForLanding(e.target.value)}
                >
                  {cabangData?.data?.map((cabang) => (
                    <MenuItem key={cabang.uuid} value={cabang.uuid}>{cabang.namacabang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
          {selectedCabangForLanding && <BranchLandingSettings cabangUuid={selectedCabangForLanding} />}
        </>
      ) : (
        <>
          <NotaBranchCard cabangUuid={branchCabangUuid} />
          <BranchLandingSettings cabangUuid={branchCabangUuid} />
          <PpnBranchCard cabangUuid={branchCabangUuid} />
        </>
      )}

      {isSuperAdmin && (
      <Card>
        <CardHeader
          title="Daftar Konfigurasi"
          action={
            <Box>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Tambah Konfigurasi
              </Button>
            </Box>
          }
        />
        
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="konfigurasi table">
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>Cabang</TableCell>
                <TableCell>No. Telepon</TableCell>
                <TableCell>Nama WiFi</TableCell>
                <TableCell>Password WiFi</TableCell>
                <TableCell>Teks A</TableCell>
                <TableCell>Teks B</TableCell>
                {/* <TableCell>Logo</TableCell> */}
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
             {konfigurasiData?.data?.length === 0 ? (
  <>
    <TableRow>
      <TableCell colSpan={8} align="center">
        <Typography variant="body1" sx={{ my: 2 }}>
          Belum ada data konfigurasi.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ mt: 1 }}
        >
          Tambah Konfigurasi
        </Button>
      </TableCell>
    </TableRow>
  </>
) : (
  konfigurasiData?.data?.map((konf, index) => (
    <TableRow key={konf.id}>
      <TableCell>{index + 1}</TableCell>
      <TableCell>{konf.Cabang?.namacabang || 'N/A'}</TableCell>
      <TableCell>{konf.noTelp || '-'}</TableCell>
      <TableCell>{konf.wifiNama || '-'}</TableCell>
      <TableCell>{konf.wifi || '-'}</TableCell>
      <TableCell>{konf.teksA || '-'}</TableCell>
      <TableCell>{konf.teksB || '-'}</TableCell>
      <TableCell>
        <IconButton color="primary" onClick={() => handleOpenDialog(konf)}>
          <EditIcon />
        </IconButton>
        <IconButton color="error" onClick={() => handleDelete(konf.id)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  ))
)}

            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      )}

      {/* Dialog for Create/Update Konfigurasi */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{currentKonfigurasi ? 'Edit Konfigurasi' : 'Tambah Konfigurasi Baru'}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {!currentKonfigurasi && (
                  // Cabang selection is only available when creating a new configuration
                  <FormControl fullWidth margin="normal" error={!!formErrors.cabangUuid}>
                    <InputLabel id="cabang-select-label">Pilih Cabang</InputLabel>
                    <Select
                      labelId="cabang-select-label"
                      id="cabang-select"
                      name="cabangUuid"
                      value={formValues.cabangUuid}
                      label="Pilih Cabang"
                      onChange={handleChange}
                    >
                      {cabangData?.data?.map((cabang) => (
                        <MenuItem key={cabang.uuid} value={cabang.uuid}>
                          {cabang.namacabang}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.cabangUuid && (
                      <Typography variant="caption" color="error">
                        {formErrors.cabangUuid}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Nomor Telepon"
                  name="noTelp"
                  value={formValues.noTelp}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Nama WiFi (SSID)"
                  name="wifiNama"
                  value={formValues.wifiNama}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Password WiFi"
                  name="wifi"
                  value={formValues.wifi}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Teks A"
                  name="teksA"
                  value={formValues.teksA}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Teks B"
                  name="teksB"
                  value={formValues.teksB}
                  onChange={handleChange}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom mt={2}>
                  Logo Konfigurasi
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="raised-button-file"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                  <Button variant="outlined" component="span">
                    Upload Logo
                  </Button>
                </label>
                {logoPreview && (
                  <Box mt={2}>
                    <Typography variant="body2">Preview:</Typography>
                    <img src={logoPreview} alt="Logo Preview" style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }} />
                  </Box>
                )}
              </Grid> */}
              {formErrors.general && (
                <Grid item xs={12}>
                  <Typography color="error">{formErrors.general}</Typography>
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Batal
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {currentKonfigurasi ? 'Update' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};